import { produce } from "immer";
import {
  ADD_TO_BASKET,
  SIGNOUT,
  CLEAR_BASKET_AFTER_ORDER,
  CLEAR_BASKET,
  EDIT_BASKET,
  DELETE_BASKET,
  DELETE_INDIVIDUAL_OPTIONS,
  EDIT_INDIVIDUAL_OPTIONS,
} from "../constants/actionTypes";
import {
  AddToBasketAction,
  DeleteBasketAction,
  EditBasketAction,
  LineItemsAction,
  LineItemType,
} from "../types/line-items";
import { ImmerActionInput } from "../types/action-types";
import {
  DeleteIndividualOptionsAction,
  DeleteOptionLineAction,
  EditIndividualOptionsAction,
} from "../types/option";
import { DELETE_OPTION_LINE } from "../constants/actionTypesTpos";

export const initialState: LineItemType[] = [];

const getNextLineItemIndex = (lineItems: LineItemType[]): number => {
  let nextIndex = 1;
  if (lineItems.length > 0) {
    nextIndex = lineItems.reduce(
      (max: number, lineItem: LineItemType) =>
        lineItem.id > max ? lineItem.id : max,
      -1,
    );
    nextIndex += 1;
  }
  return nextIndex;
};

const addToBasket = ({
  action,
  draft,
}: ImmerActionInput<AddToBasketAction, LineItemType[]>) => {
  const { item, variationId, quantity, comboSelections } = action.payload;
  if (comboSelections) {
    const existingIndex = draft.findIndex(
      (basketItem) =>
        basketItem.productId === item.id &&
        basketItem.variationId === variationId &&
        JSON.stringify(basketItem.comboSelections) ===
          JSON.stringify(comboSelections),
    );
    if (existingIndex !== -1) {
      draft[existingIndex].quantity += quantity;
    } else {
      draft.push({
        id: getNextLineItemIndex(draft),
        variationId,
        productId: item.id,
        quantity,
        comboSelections,
      });
    }
    return;
  }

  const nextIndex = getNextLineItemIndex(draft);

  const basketItemIndex = draft.findIndex(
    (basketItem) =>
      basketItem.productId === item.id &&
      basketItem.variationId === variationId,
  );
  if (basketItemIndex === -1) {
    draft.push({
      id: nextIndex,
      variationId,
      productId: item.id,
      quantity,
    });
  } else {
    const basketItem = draft[basketItemIndex];
    draft[basketItemIndex] = {
      ...basketItem,
      quantity: basketItem.quantity + quantity,
    };
  }
};

const editBasket = ({
  action,
  draft,
}: ImmerActionInput<EditBasketAction, LineItemType[]>) => {
  const { lineId, variationId, item, quantity } = action.payload;
  let modifiedIndex;
  let unmodifiedIndex = -1;
  const newDraft: LineItemType[] = [];
  draft.forEach((lineItem, index) => {
    if (`${lineItem.id}` === lineId) {
      newDraft.push({ ...lineItem, variationId, quantity });
      modifiedIndex = index;
    } else if (
      item.id === lineItem.productId &&
      lineItem.variationId === variationId &&
      !lineItem.parentId &&
      `${lineItem.id}` !== lineId
    ) {
      newDraft.push(lineItem);
      unmodifiedIndex = index;
    } else newDraft.push(lineItem);
  });
  if (
    unmodifiedIndex >= 0 &&
    modifiedIndex &&
    newDraft[modifiedIndex].variationId ===
      newDraft[unmodifiedIndex].variationId
  ) {
    newDraft[modifiedIndex].quantity += newDraft[unmodifiedIndex].quantity;
    newDraft.splice(unmodifiedIndex, 1);
  }
  return newDraft;
};

const deleteBasket = ({
  action,
  draft,
}: ImmerActionInput<DeleteBasketAction, LineItemType[]>) => {
  const { lineId } = action.payload;
  return draft.filter((lineItem) => `${lineItem.id}` !== lineId);
};

const editIndividualOptions = ({
  action,
  draft,
}: ImmerActionInput<EditIndividualOptionsAction, LineItemType[]>) => {
  const lineId = action.payload;
  const line = draft.find((lineItem) => lineItem.id === lineId);
  if (!line) {
    return draft;
  }
  const copiedLines = draft.filter(
    (lineItem) =>
      lineItem.productId === line.productId && lineItem.parentId === line.id,
  );
  const quantityDifference = line.quantity - copiedLines.length;

  if (quantityDifference === 0) {
    return draft;
  } else if (quantityDifference < 0) {
    let quantityCount = 0;
    const newDraft = draft
      .filter((lineItem) => {
        if (
          lineItem.productId === line.productId &&
          lineItem.parentId === line.id &&
          quantityCount < line.quantity
        ) {
          quantityCount += 1;
          return true;
        } else if (
          lineItem.productId === line.productId &&
          lineItem.parentId === line.id &&
          quantityCount >= line.quantity
        ) {
          return false;
        } else {
          return true;
        }
      })
      .map((lineItem) => {
        if (
          lineItem.productId === line.productId &&
          !lineItem.parentId &&
          lineItem.children
        ) {
          return {
            ...lineItem,
            children: lineItem.children.filter(
              (i, index) => index < lineItem.quantity,
            ),
          };
        }
        return lineItem;
      });
    return newDraft;
  }
  if (!line.children) line.children = [];
  const newDraft = draft;
  Array(quantityDifference)
    .fill(0)
    .forEach(() => {
      if (!line.children) {
        line.children = [];
      }
      const nextIndex = getNextLineItemIndex(draft);
      line.children.push(nextIndex);
      const lineItem = {
        ...line,
        id: nextIndex,
        productId: line.productId,
        parentId: line.id,
      };
      draft.push(lineItem);
    });
  return newDraft;
};

const deleteIndividualOptions = ({
  action,
  draft,
}: ImmerActionInput<DeleteIndividualOptionsAction, LineItemType[]>) => {
  const lineId = action.payload;
  const line = draft.find((lineItem) => lineItem.id === lineId);
  if (!line) {
    return draft;
  }
  const newDraft = draft
    .filter(
      (lineItem) =>
        !(
          lineItem.productId === line.productId && lineItem.parentId === line.id
        ),
    )
    .map((lineItem) => {
      if (lineItem.productId === line.productId) {
        return { ...lineItem, children: undefined };
      }
      return lineItem;
    });
  return newDraft;
};

const deleteLinesByParentId = (
  draft: LineItemType[],
  parentId: number,
): LineItemType[] => {
  const linesToDelete = draft.filter(
    (lineItem) => lineItem.parentId === parentId,
  );

  if (linesToDelete.length === 0) {
    return draft;
  }

  return draft
    .filter((lineItem) => lineItem.parentId !== parentId)
    .map((lineItem) => {
      if (lineItem.id === parentId && lineItem.children) {
        return {
          ...lineItem,
          children: lineItem.children.filter(
            (child) =>
              !linesToDelete.some((deletedLine) => deletedLine.id === child),
          ),
          quantity: lineItem.quantity - linesToDelete.length,
        };
      }
      return lineItem;
    });
};

const deleteOptionLine = ({
  action,
  draft,
}: ImmerActionInput<DeleteOptionLineAction, LineItemType[]>) => {
  const lineId = action.payload;
  const lineToDelete = draft.find((lineItem) => lineItem.id === lineId);

  if (!lineToDelete) {
    return deleteLinesByParentId(draft, lineId);
  }

  let newDraft = deleteLinesByParentId(draft, lineId);

  newDraft = newDraft
    .filter((lineItem) => lineItem.id !== lineId)
    .map((lineItem) => {
      if (lineItem.id === lineToDelete.parentId && lineItem.children) {
        return {
          ...lineItem,
          children: lineItem.children.filter((child) => child !== lineId),
          quantity: lineItem.quantity - 1,
        };
      }
      return lineItem;
    });
  return newDraft;
};

export const lineItemsReducer = produce(
  (draft: LineItemType[], action: LineItemsAction) => {
    switch (action.type) {
      case ADD_TO_BASKET:
        return addToBasket({ action, draft });
      case EDIT_BASKET:
        return editBasket({ action, draft });
      case DELETE_BASKET:
        return deleteBasket({ action, draft });
      case EDIT_INDIVIDUAL_OPTIONS:
        return editIndividualOptions({ action, draft });
      case DELETE_INDIVIDUAL_OPTIONS:
        return deleteIndividualOptions({ action, draft });
      case DELETE_OPTION_LINE:
        return deleteOptionLine({ action, draft });
      case SIGNOUT:
      case CLEAR_BASKET_AFTER_ORDER:
      case CLEAR_BASKET:
        return [];
      default:
    }
  },
  initialState,
);

export default lineItemsReducer;
